import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { defaultContactContent } from "@quintara/site-kit";
import {
  CONTACT_API_URL,
  CONTACT_EMAIL,
  COUNTRIES,
  SEGMENTS,
  TURNSTILE_SITE_KEY,
  VOLUMES,
} from "../constants";
import { submitContact } from "../api";
import { useCmsContent } from "../useCmsContent";
import { usePageMeta } from "../usePageMeta";

// Minimal typing for the Cloudflare Turnstile global injected by their script.
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      getResponse: (id?: string) => string | undefined;
      reset: (id?: string) => void;
    };
    quintaraTurnstileReady?: () => void;
  }
}

const REQUIRED = ["name", "email", "company", "country", "segment", "message"] as const;
const MAX_LEN: Record<string, number> = { name: 100, email: 254, company: 150, message: 5000 };
const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);

// Mirror the original site's readiness gate: while either public value is still
// a placeholder, the form shows the email fallback instead of trying to send.
const API_READY =
  /^https:\/\/[A-Za-z0-9.-]+(:\d+)?\/([A-Za-z0-9._~-]+\/)*contact$/.test(CONTACT_API_URL) ||
  /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/contact$/.test(CONTACT_API_URL);
const KEY_READY = /^[A-Za-z0-9_-]{10,100}$/.test(TURNSTILE_SITE_KEY) && !TURNSTILE_SITE_KEY.includes("REPLACE");
const CONFIGURED = API_READY && KEY_READY;

type Status = { kind: "ok" | "bad"; content: ReactNode };

function CopyButton({ value }: { value: string }) {
  const [label, setLabel] = useState("Copy");
  async function copy() {
    const done = () => {
      setLabel("Copied");
      setTimeout(() => setLabel("Copy"), 1600);
    };
    try {
      await navigator.clipboard.writeText(value);
      done();
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — surface the text.
      setLabel("Select and copy");
    }
  }
  return (
    <button className="copy-btn" type="button" onClick={copy}>
      {label}
    </button>
  );
}

// Shared styled dropdown used by every <select> on the form, so they all look
// identical (a native <select>/<datalist> popup can't be styled). `searchable`
// turns on type-to-filter (country); otherwise it's a click-to-open picker
// (segment, volume). The <input> keeps its name so it submits via FormData.
function Select({
  id,
  name,
  value,
  onChange,
  options,
  placeholder,
  searchable = false,
}: {
  id: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const box = useRef<HTMLDivElement>(null);

  const q = searchable ? value.trim().toLowerCase() : "";
  const matches = (q ? options.filter((o) => o.toLowerCase().includes(q)) : options).slice(0, 60);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (o: string) => {
    onChange(o);
    setOpen(false);
    setHi(-1);
  };

  return (
    <div className="combo" ref={box}>
      <input
        id={id}
        name={name}
        value={value}
        readOnly={!searchable}
        autoComplete="off"
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete={searchable ? "list" : "none"}
        aria-controls={`${id}-list`}
        onChange={searchable ? (e) => { onChange(e.target.value); setOpen(true); setHi(-1); } : undefined}
        onFocus={() => setOpen(true)}
        onMouseDown={searchable ? undefined : () => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setHi((i) => Math.min(i + 1, matches.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHi((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter" && open && matches.length) {
            e.preventDefault();
            if (hi >= 0) pick(matches[hi]);
            else if (searchable) pick(matches[0]);
            else setOpen(false);
          } else if (e.key === "Escape") {
            setOpen(false);
          } else if (!searchable && e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      />
      {open && (
        <ul className="combo-list" id={`${id}-list`} role="listbox">
          {matches.length === 0 ? (
            <li className="combo-empty">No matches</li>
          ) : (
            matches.map((o, i) => (
              <li
                key={o}
                role="option"
                aria-selected={i === hi || (hi < 0 && o === value)}
                onMouseEnter={() => setHi(i)}
                onMouseDown={(e) => {
                  e.preventDefault(); // beat the input's blur so the pick registers
                  pick(o);
                }}
              >
                {o}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export function Contact() {
  usePageMeta("Contact Quintara", "Talk to the Quintara team about WhatsApp business messaging for your organization.");

  // Editable copy (hero + contact details) from the CMS; the form stays code.
  const copy = useCmsContent("contact", defaultContactContent);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [consentError, setConsentError] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [status, setStatus] = useState<Status | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitLabel, setSubmitLabel] = useState("Send enquiry");
  const [country, setCountry] = useState("");
  const [segment, setSegment] = useState("");
  const [volume, setVolume] = useState("");

  const startedAt = useRef(Date.now());
  const captchaBox = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const rendered = useRef(false);

  // Load + render Turnstile once, on the contact page only (explicit render via
  // the script's onload callback). Guarded against the double-invoke that React
  // StrictMode does in dev, and against injecting the script twice.
  useEffect(() => {
    if (!CONFIGURED) return;

    const renderWidget = () => {
      if (rendered.current || !window.turnstile || !captchaBox.current) return;
      rendered.current = true;
      widgetId.current = window.turnstile.render(captchaBox.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action: "contact",
        theme: "light",
        size: captchaBox.current.clientWidth < 300 ? "compact" : "flexible",
        "refresh-expired": "auto",
        callback: () => setCaptchaError(""),
        "error-callback": () =>
          setCaptchaError("The security check couldn't load. Refresh the page, or email us instead."),
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }
    window.quintaraTurnstileReady = renderWidget;
    if (!document.getElementById("cf-turnstile-script")) {
      const s = document.createElement("script");
      s.id = "cf-turnstile-script";
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=quintaraTurnstileReady";
      s.async = true;
      s.defer = true;
      s.onerror = () =>
        setCaptchaError("The security check couldn't load. Check your connection or ad blocker, or email us instead.");
      document.head.appendChild(s);
    }
  }, []);

  const captchaToken = () => {
    try {
      return widgetId.current !== null && window.turnstile ? window.turnstile.getResponse(widgetId.current) || "" : "";
    } catch {
      return "";
    }
  };
  const resetCaptcha = () => {
    try {
      if (widgetId.current !== null && window.turnstile) window.turnstile.reset(widgetId.current);
    } catch {
      /* ignore */
    }
  };

  const fail = (content: ReactNode) => {
    resetCaptcha(); // a Turnstile token is single-use — get a fresh one for the next attempt
    setStatus({ kind: "bad", content });
    setSending(false);
    setSubmitLabel("Send enquiry");
  };

  function applyServerErrors(form: HTMLFormElement, fields: Record<string, string> | undefined) {
    const next: Record<string, string> = {};
    let firstBad: string | null = null;
    Object.keys(fields || {}).forEach((id) => {
      const code = (fields as Record<string, string>)[id];
      if (id === "consent") {
        setConsentError("Please confirm you have read the privacy policy.");
        if (!firstBad) firstBad = "consent";
        return;
      }
      next[id] =
        code === "required"
          ? "Required."
          : code === "too_long"
            ? "This is too long."
            : id === "email"
              ? "Enter a valid work email, like name@company.com."
              : "Please check this field.";
      if (!firstBad) firstBad = id;
    });
    setErrors(next);
    if (firstBad && firstBad !== "consent") (form.elements.namedItem(firstBad) as HTMLElement | null)?.focus();
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending) return; // ignore double clicks / repeated Enter while a request is in flight
    const form = e.currentTarget;
    const fd = new FormData(form);
    const get = (k: string) => String(fd.get(k) ?? "").trim();

    const nextErrors: Record<string, string> = {};
    let firstBad: string | null = null;
    for (const id of REQUIRED) {
      const value = get(id);
      let msg = "";
      if (!value) msg = "Required.";
      else if (id === "email" && !emailOk(value)) msg = "Enter a valid work email, like name@company.com.";
      else if (MAX_LEN[id] && value.length > MAX_LEN[id]) msg = `This is too long (${MAX_LEN[id]} characters max).`;
      if (msg) {
        nextErrors[id] = msg;
        if (!firstBad) firstBad = id;
      }
    }

    // The country field is a free-text input backed by a <datalist>, so match
    // what was typed to the backend's accepted list (case-insensitively) and
    // send the canonical spelling. Reject anything not on the list.
    const countryCanonical = COUNTRIES.find((c) => c.toLowerCase() === get("country").toLowerCase()) ?? "";
    if (get("country") && !countryCanonical && !nextErrors.country) {
      nextErrors.country = "Select a country from the list.";
      if (!firstBad) firstBad = "country";
    }

    const consentChecked = fd.get("consent") === "yes";
    const consentMsg = consentChecked ? "" : "Please confirm you have read the privacy policy.";

    setErrors(nextErrors);
    setConsentError(consentMsg);

    if (firstBad) {
      (form.elements.namedItem(firstBad) as HTMLElement | null)?.focus();
      return;
    }
    if (consentMsg) {
      (form.elements.namedItem("consent") as HTMLElement | null)?.focus();
      return;
    }

    setStatus(null);

    if (!CONFIGURED) {
      setStatus({
        kind: "bad",
        content: (
          <>
            Online sending is not switched on yet. Email us at <b>{CONTACT_EMAIL}</b> and we will reply within one
            business day.
          </>
        ),
      });
      return;
    }

    const token = captchaToken();
    if (!token) {
      setCaptchaError("Please complete the security check above the Send button.");
      return;
    }
    setCaptchaError("");

    const payload = {
      name: get("name"),
      email: get("email"),
      company: get("company"),
      phone: get("phone"),
      country: countryCanonical,
      segment: get("segment"),
      volume: get("volume"),
      message: get("message"),
      consent: consentChecked ? "yes" : "",
      website: String(fd.get("website") ?? ""),
      elapsed_ms: Math.max(0, Math.round(Date.now() - startedAt.current)),
      turnstile_token: token,
    };

    setSending(true);
    setSubmitLabel("Sending…");

    const result = await submitContact(payload);

    if (result.ok) {
      const firstName = (payload.name || "").split(" ")[0];
      form.reset();
      setCountry(""); // controlled fields — form.reset() doesn't clear them
      setSegment("");
      setVolume("");
      resetCaptcha();
      setErrors({});
      setStatus({
        kind: "ok",
        content: (
          <>
            <b>Thanks, {firstName}.</b> Your enquiry reached our team. We reply within one business day.
          </>
        ),
      });
      setSent(true); // button stays disabled after a confirmed send
      setSubmitLabel("Sent");
      return;
    }

    const code = result.error;
    if (code === "timeout") {
      fail(
        <>
          The request took too long. Please try again, or email <b>{CONTACT_EMAIL}</b>.
        </>,
      );
    } else if (code === "network") {
      fail(
        <>
          We couldn't send your enquiry. Check your connection and try again, or email <b>{CONTACT_EMAIL}</b>.
        </>,
      );
    } else if (code === "validation") {
      fail(<>Please check the highlighted fields and try again.</>);
      applyServerErrors(form, result.fields);
    } else if (code === "captcha_missing" || code === "captcha_invalid" || code === "captcha_expired") {
      setCaptchaError("The security check expired or didn't pass. Please complete it again.");
      fail(<>Please complete the security check again, then send.</>);
    } else if (code === "too_fast") {
      fail(<>That was quicker than we expected. Please wait a few seconds and send again.</>);
    } else if (code === "rate_limited") {
      fail(
        <>
          You've sent several enquiries in a short time. Please try again in a few minutes, or email{" "}
          <b>{CONTACT_EMAIL}</b>.
        </>,
      );
    } else {
      fail(
        <>
          We couldn't send your enquiry just now. Please try again in a moment, or email <b>{CONTACT_EMAIL}</b>.
        </>,
      );
    }
  }

  const fieldClass = (id: string) => (errors[id] ? "field invalid" : "field");

  return (
    <>
      <section className="hero page-hero">
        <div className="wrap">
          <div className="hero-copy">
            <p className="eyebrow">{copy.hero.eyebrow}</p>
            <h1>{copy.hero.heading}</h1>
            <p className="lede">{copy.hero.lede}</p>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap form-grid">
          <form id="contact-form" noValidate onSubmit={onSubmit}>
            {/* Honeypot: hidden from people, bots tend to fill it. The backend
                rejects any submission where "website" is non-empty. Pushed
                off-screen and out of tab order rather than display:none. */}
            <div
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", top: "auto", height: 0, width: 0, overflow: "hidden" }}
            >
              <label htmlFor="website">Leave this field empty</label>
              <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
            </div>
            <div className="row2">
              <div className={fieldClass("name")}>
                <label htmlFor="name">Full name</label>
                <input id="name" name="name" autoComplete="name" maxLength={MAX_LEN.name} />
                <span className="error" aria-live="polite">{errors.name}</span>
              </div>
              <div className={fieldClass("email")}>
                <label htmlFor="email">Work email</label>
                <input id="email" name="email" type="email" autoComplete="email" maxLength={MAX_LEN.email} />
                <span className="error" aria-live="polite">{errors.email}</span>
              </div>
            </div>
            <div className="row2">
              <div className={fieldClass("company")}>
                <label htmlFor="company">Company or organization</label>
                <input id="company" name="company" autoComplete="organization" maxLength={MAX_LEN.company} />
                <span className="error" aria-live="polite">{errors.company}</span>
              </div>
              <div className="field">
                <label htmlFor="phone">Phone <span className="opt">(optional)</span></label>
                <input id="phone" name="phone" type="tel" autoComplete="tel" maxLength={30} />
              </div>
            </div>
            <div className="row2">
              <div className={fieldClass("country")}>
                <label htmlFor="country">Country</label>
                <Select
                  id="country"
                  name="country"
                  value={country}
                  onChange={setCountry}
                  options={COUNTRIES}
                  placeholder="Start typing a country"
                  searchable
                />
                <span className="error" aria-live="polite">{errors.country}</span>
              </div>
              <div className={fieldClass("segment")}>
                <label htmlFor="segment">You are</label>
                <Select id="segment" name="segment" value={segment} onChange={setSegment} options={SEGMENTS} placeholder="Select one" />
                <span className="error" aria-live="polite">{errors.segment}</span>
              </div>
            </div>
            <div className="field">
              <label htmlFor="volume">Monthly messages today, SMS or other channels <span className="opt">(optional)</span></label>
              <Select id="volume" name="volume" value={volume} onChange={setVolume} options={VOLUMES} placeholder="Select a range" />
            </div>
            <div className={fieldClass("message")}>
              <label htmlFor="message">What would you like to do on WhatsApp?</label>
              <textarea
                id="message"
                name="message"
                maxLength={MAX_LEN.message}
                placeholder="For example: move our OTP traffic off SMS in the UAE and Saudi Arabia, and add a sales assistant for Arabic-speaking customers."
              ></textarea>
              <span className="error" aria-live="polite">{errors.message}</span>
            </div>
            <div className="field">
              <label className="consent" htmlFor="consent">
                <input id="consent" name="consent" type="checkbox" value="yes" />
                <span>
                  I agree that Quintara may use these details to respond to my enquiry, as described in the{" "}
                  <Link to="/privacy">privacy policy</Link>.
                </span>
              </label>
              <span className="error" aria-live="polite">{consentError}</span>
            </div>
            {/* Cloudflare Turnstile widget mounts here (contact page only). */}
            <div className={captchaError ? "field invalid" : "field"} id="captcha-field" hidden={!CONFIGURED}>
              <div ref={captchaBox} id="cf-turnstile" />
              <span className="error" aria-live="polite">{captchaError}</span>
            </div>
            <div>
              <button className="btn btn-primary" type="submit" disabled={sending || sent}>
                {submitLabel}
              </button>
            </div>
            {status && (
              <div className={status.kind === "ok" ? "form-status ok" : "form-status bad"} role="status">
                {status.content}
              </div>
            )}
          </form>

          <aside className="contact-side">
            <dl>
              <div>
                <dt>Email</dt>
                <dd>
                  <span>{copy.side.email}</span>
                  <CopyButton value={copy.side.email} />
                </dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>
                  <span>{copy.side.phone}</span>
                  <CopyButton value={copy.side.phone} />
                </dd>
              </div>
              <div>
                <dt>Office</dt>
                <dd>
                  {copy.side.officeName}
                  <br />
                  {copy.side.officeLocation}
                </dd>
              </div>
            </dl>
            <p className="small muted">{copy.side.note}</p>
          </aside>
        </div>
      </section>
    </>
  );
}
